"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const EventSender_1 = __importDefault(require("./EventSender"));
const SessionManager_1 = __importDefault(require("./utilities/SessionManager"));
const SequenceNumberHandler_1 = __importDefault(require("./utilities/SequenceNumberHandler"));
const GAScriptLoader_1 = __importDefault(require("./utilities/GAScriptLoader"));
const PlatformEnum_1 = __importDefault(require("./enums/PlatformEnum"));
const ModuleClient_1 = __importDefault(require("./ModuleClient"));
const CookieReader_1 = __importDefault(require("./utilities/CookieReader"));
const MetadataManager_1 = __importDefault(require("./utilities/MetadataManager"));
/**
 * Entrypoint API class for applications.
 */
class InsightsApi {
    constructor() {
        // eslint-disable-next-line
        this._isEnabled = false;
        this.eventSender = new EventSender_1.default();
        this.session = new SessionManager_1.default();
        this.sequenceNumber = new SequenceNumberHandler_1.default();
    }
    /**
     *	Returns a boolean that indicates whether or not insights api is enabled.
     */
    get isEnabled() {
        return this._isEnabled;
    }
    /**
     * Connect the component for an application.
     *
     * @param props Initialization data for the application.
     */
    connectApplication(props) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof props.applicationId !== 'string') {
                throw new Error('Property applicationId is not a string. DPC Initialization will not proceed.');
            }
            if (!Object.values(PlatformEnum_1.default).includes(props.platform)) {
                throw Error(`Platform ${props.platform} is not valid. Check PlatformEnum. DPC Initialization will not proceed.`);
            }
            if (props.kiosk) {
                props.platform = PlatformEnum_1.default.kiosk;
            }
            MetadataManager_1.default.registerApplication(props);
            if (props.enableGa === true) {
                const cookieReader = new CookieReader_1.default();
                try {
                    cookieReader.getGaId();
                    const scriptLoader = new GAScriptLoader_1.default();
                    yield scriptLoader.loadGoogleAnalyticsScript();
                    yield this.eventSender.setupAndSendInit(props);
                }
                catch (error) {
                    // Disable GA in case we cannot find the cookie.
                    // eslint-disable-next-line
                    console.warn(`GA data not found. Disabling GA events.`);
                    MetadataManager_1.default.updateApplication({ enableGa: false });
                    // Send regular ApplicationStartUpEvents instead.
                    yield this.eventSender.sendApplicationStartUpEvents();
                }
            }
            else {
                yield this.eventSender.sendApplicationStartUpEvents();
            }
            this._isEnabled = true;
        });
    }
    /**
     * Receive module initialization data and
     * returns instance of module client.
     *
     * @param moduleMetadata Initialization data for the module.
     * @returns The module client instance.
     */
    moduleClient(moduleMetadata) {
        if (typeof moduleMetadata.moduleId !== 'string') {
            throw new Error('Property moduleId is not a string. Module client initialization will not proceed.');
        }
        if (!this.isEnabled) {
            throw new Error('Application is not enabled. Module client initialization will not proceed.');
        }
        const applicationMetadata = MetadataManager_1.default.getApplicationMetadata();
        if (!applicationMetadata || Object.keys(applicationMetadata).length === 0) {
            throw new Error('Application metadata not found. Module client initialization will not proceed.');
        }
        return new ModuleClient_1.default(moduleMetadata, this);
    }
    /**
     * Disable the component. Will reset session data and metadata, as well as the sequence number.
     */
    disconnect() {
        this._isEnabled = false;
        this.session.clearSessions();
        this.sequenceNumber.resetSequenceNumber();
        MetadataManager_1.default.clear();
    }
    /**
     * Send an addToCart event, if the component is enabled.
     *
     * @param data The included items in the cart.
     */
    addToCart(data) {
        if (this.isEnabled) {
            this.eventSender.sendAddToCart(data);
        }
    }
    /**
     * Send an addToWishList event, if the component is enabled.
     *
     * @param data The included items in the wish list.
     */
    addToWishList(data) {
        if (this.isEnabled) {
            this.eventSender.sendAddToWishList(data);
        }
    }
    /**
     * Send an initPlanningSession event, if the component is enabled.
     *
     * @param designId The design id that started the session.
     * @param source The source of the planning session.
     * @param [payload] Optional payload data.
     * @param [spr] Optional The SPR that was used as base for the planning if any.
     *
     */
    initPlanningSession(designId, source, payload, spr) {
        if (this.isEnabled) {
            this.eventSender.sendInitPlanningSession(designId, source, payload, spr);
        }
    }
    /**
     * Send a savePlanner event, if the component is enabled.
     *
     * @param designId The design id to save, optional.
     * @param [payload] Optional payload data.
     * @param [mode] How this design was saved or shared.
     */
    saveDesign(designId, payload, mode) {
        if (this.isEnabled) {
            this.eventSender.sendSavePlanner(designId, payload, mode);
        }
    }
    /**
     * Send a custom event, if the component is enabled.
     *
     * @param data The information about the custom event.
     */
    sendEvent(data) {
        if (this.isEnabled) {
            this.eventSender.sendCustomEvent(data);
        }
    }
    /**
     * Send a design interaction event, if the component is enabled.
     *
     * @param designId The design id which was interacted with.
     * @param interaction The interaction source.
     * @param [payload] Optional payload data.
     */
    sendDesignInteractionEvent(designId, interaction, payload) {
        if (this.isEnabled) {
            this.eventSender.sendDesignInteractionEvent(designId, interaction, payload);
        }
    }
    /**
     * Send a article action event, if the component is enabled.
     *
     * @param articleId The article id.
     * @param action The action being performed.
     * @param isGlobal Indicate if article id is global or local.
     * @param [payload] Optional payload data.
     */
    articleAction(articleId, action, isGlobal, payload) {
        if (this.isEnabled) {
            this.eventSender.sendArticleAction(articleId, action, isGlobal, payload);
        }
    }
    /**
     * Sets the A/B version for the session to facilitate A/B testing, if the component is enabled.
     *
     * @param abVersionName Name of the ab version.
     * @param abVersionVariation Optional ab variation.
     */
    setAbVersion(abVersionName, abVersionVariation) {
        if (this.isEnabled) {
            MetadataManager_1.default.updateApplication({ abVersionName, abVersionVariation });
            this.eventSender.sendSetAbVersion();
        }
    }
    /**
     * Send a flow step event, if the component is enabled.
     *
     * @param flowName Name of the flow.
     * @param stepName Name of the step in the flow.
     * @param stepLevel Level of step in the flow.
     */
    sendUserFlowStep(flowName, stepName, stepLevel, payload) {
        if (this.isEnabled) {
            this.eventSender.sendUserFlowStep(flowName, stepName, stepLevel, payload);
        }
    }
    /**
     * Send a flow step event, if the component is enabled.
     * Asynchronous version of this function.
     *
     * @param flowName Name of the flow.
     * @param stepName Name of the step in the flow.
     * @param stepLevel Level of step in the flow.
     */
    sendUserFlowStepAsync(flowName, stepName, stepLevel, payload) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isEnabled) {
                yield this.eventSender.sendUserFlowStep(flowName, stepName, stepLevel, payload);
            }
        });
    }
    /**
     * Get user ids.
     *
     * @returns The user ids (sessionId, planningId, longTermId and ipexId).
     */
    getIds() {
        if (this.isEnabled) {
            return this.session.getIds();
        }
    }
}
exports.default = InsightsApi;
//# sourceMappingURL=InsightsApi.js.map